import { name } from "./__init__";
import { createSelector } from "reselect";
import moment from "moment";
import constants from "../../utils/constants";
import {
  calculateServiceRate,
  getCityTaxConfiguration,
  getExchangeMultiplier,
  transformBookableWithCurrencyConversion,
} from "../../utils/misc";
import { fromJS, Map, List } from "immutable";
import { smartPaymentModeSelector } from "../BookingConfirmationSmartPayment/selectors";
import { dateDiffInDays } from "../../utils/date";

const TAX_MULTIPLIER = 0.0909090909;

export const getModelSelector = state => state[name];
/* TODO: this is almost a hack to avoid circular dependencies  */
export const getAppModelSelector = state => state.app;

export const restoredSessionSelector = createSelector(
  getAppModelSelector,
  model => model && model.get("restoredSession")
);

export const configurationSelector = createSelector(
  getAppModelSelector,
  model => model && model.get(constants.CONFIGURATION_KEY)
);

export const smartPaymentAvailablePaymentsSelector = createSelector(
  getModelSelector,
  model => model && model.get("smartPaymentAvailablePayments")
);

export const invalidPaymentsSelector = createSelector(
  getModelSelector,
  model => model && model.get("invalidPayments")
);

export const currencySelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("currency")
);

export const currencyExchangeSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("currencyExchange")
);

export const propertyTickerSelector = createSelector(
  getAppModelSelector,
  model => model && model.get("propertyTicker")
);

export const propertyDataSelector = createSelector(
  getAppModelSelector,
  model => model && model.get(constants.PROPERTY_DATA_KEY)
);

export const reservationsSelector = createSelector(
  getModelSelector,
  model => model && model.get("reservations")
);

export const invalidReservationsSelector = createSelector(
  getModelSelector,
  model => model && model.get("invalidReservations")
);

export const reservationARIMapSelector = createSelector(
  getModelSelector,
  model => model && model.get("reservationARIMap")
);

export const selectedPaymentTickerSelector = createSelector(
  getModelSelector,
  propertyTickerSelector,
  model => model && model.get("paymentTypes") && model.get("paymentTypes").get("selected")
);

export const defaultCurrencySelector = createSelector(
  propertyTickerSelector,
  propertyDataSelector,
  configurationSelector,
  (propertyTicker, propertyData, configuration) =>
    (propertyTicker &&
      propertyData &&
      propertyData.get(propertyTicker) &&
      propertyData.get(propertyTicker).get("defaultCurrency")) ||
    (configuration && configuration.get("defaultCurrency"))
);

export const paymentTypeDataSelector = createSelector(reservationARIMapSelector, data => data);

export const addObligatoryExtrasRateToRoomSelectionSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("addObligatoryExtrasRateToRoomSelection")
);

export const denormalizedInvalidReservationsSelector = createSelector(
  invalidReservationsSelector,
  reservationARIMapSelector,
  (reservations, reservationARIMap) => {
    if (!reservations || reservations.size === 0) {
      return null;
    }
    const denormalizedReservations = reservations.map(reservation => {
      const propertyARI = reservationARIMap.get(reservation.get("id"));
      const propertyTicker = reservation.get("propertyTicker");
      /* TODO: check if I can use the APP selectors */
      const inventories =
        propertyARI &&
        propertyARI.get(propertyTicker) &&
        propertyARI.get(propertyTicker).get("inventories");
      const services =
        propertyARI &&
        propertyARI.get(propertyTicker) &&
        propertyARI.get(propertyTicker).get("services");
      let inventory = inventories.get(reservation.get("inventory"));
      /* TODO: sort services by order */
      inventory = inventory.set(
        "services",
        (inventory.get("services") &&
          inventory
            .get("services")
            .map(
              ticker =>
                services.get(ticker) &&
                services
                  .get(ticker)
                  .merge(propertyARI.get(propertyTicker).get("arivalues").get(ticker))
            )
            .filter(service => !!service)) ||
          []
      );
      inventory = inventory.set(
        "accommodation",
        propertyARI.get(propertyTicker).get("accommodations").get(inventory.get("accommodation"))
      );
      inventory = inventory.set(
        "mealplan",
        propertyARI.get(propertyTicker).get("mealplans").get(inventory.get("mealplan"))
      );

      return reservation.set("inventory", inventory);
    });
    return denormalizedReservations;
  }
);

function calculateServicesRate({
  services,
  rooms,
  stayRate,
  defaultCurrency,
  currencyExchange,
  inventoryTicker,
  propertyARI,
}) {
  return services.reduce((accumulator, service) => {
    return (
      accumulator +
      calculateServiceRate({
        service,
        rooms,
        transformCurrency: false,
        stayRate,
        defaultCurrency,
        currencyExchange,
        inventoryTicker,
        propertyARI,
      })
    );
  }, 0);
}

function calculateDeposit(
  reservationTotalRate,
  paymentTypePaymentPercentage,
  payFirstNight,
  earlyCharge,
  minimumCharge,
  firstNightCost,
  numberOfUnits,
  negativeServicesTotalAmount
) {
  const paymentTypePaymentPercentagePrice =
    earlyCharge && earlyCharge > 0
      ? 0
      : (paymentTypePaymentPercentage / 100) * reservationTotalRate;
  const firstNightPrice =
    (payFirstNight &&
      firstNightCost &&
      firstNightCost > 0 &&
      firstNightCost * numberOfUnits + negativeServicesTotalAmount) ||
    0;
  let earlyChargePrice = earlyCharge ? (earlyCharge / 100) * reservationTotalRate : 0;
  earlyChargePrice =
    earlyCharge && earlyChargePrice < minimumCharge
      ? Math.min(minimumCharge, reservationTotalRate)
      : earlyChargePrice;
  const minimumChargePrice =
    minimumCharge && minimumCharge > 0 && minimumCharge < reservationTotalRate
      ? minimumCharge * numberOfUnits
      : 0;
  let reservationDepositAmount = Math.max(firstNightPrice, earlyChargePrice, minimumChargePrice);
  reservationDepositAmount =
    reservationDepositAmount > 0 ? reservationDepositAmount : paymentTypePaymentPercentagePrice;
  return reservationDepositAmount;
}

export function getSpecificQuestionnaireType(
  propertyData,
  propertyTicker,
  type,
  customConditionCallback
) {
  // comment
  return (
    propertyData.getIn([propertyTicker, "questionnaires"]) &&
    propertyData
      .getIn([propertyTicker, "questionnaires"])
      .filter(questionnaire => questionnaire.get("active"))
      .filter(questionnaire => {
        const result2 = questionnaire
          .get("dependers")
          .filter(
            depender =>
              depender.get("type") === type &&
              (customConditionCallback ? customConditionCallback(depender) : true)
          );
        return result2.size > 0;
      })
      .toJS()
  );
}
const getCurrencyFromReservationARIMap = ({ reservationARIMap, reservation }) => {
  const reservationARI = reservationARIMap.get(reservation.get("id"));
  const propertyTicker = reservation.get("propertyTicker");
  return reservationARI.get(propertyTicker).get("arivalues").get(propertyTicker).get("currency");
};

const getEntitiesFromReservationARIMap = ({ reservationARIMap, reservation }) => {
  const reservationARI = reservationARIMap.get(reservation.get("id"));
  const propertyTicker = reservation.get("propertyTicker");
  const inventories =
    reservationARI &&
    reservationARI.get(propertyTicker) &&
    reservationARI.get(propertyTicker).get("inventories");
  const services =
    reservationARI &&
    reservationARI.get(propertyTicker) &&
    reservationARI.get(propertyTicker).get("services");
  const packages =
    reservationARI &&
    reservationARI.get(propertyTicker) &&
    reservationARI.get(propertyTicker).get("packages");
  return { inventories, services, reservationARI, packages };
};

export const availablePaymentsSelector = createSelector(
  propertyTickerSelector,
  reservationsSelector,
  reservationARIMapSelector,
  smartPaymentModeSelector,
  smartPaymentAvailablePaymentsSelector,
  (
    /** JSON wrote at the backoffice*/
    propertyTicker,
    reservations,
    reservationARIMap,
    smartPaymentMode,
    smartPaymentAvailablePayments
  ) => {
    if (smartPaymentMode) {
      return smartPaymentAvailablePayments;
    }
    if (!reservations) {
      return null;
    }
    const daystoMilisecondsConvertor = 3600 * 24 * 1000;
    const hourstoMilisecondsConvertor = 3600 * 1000;
    let chronoUnitConversorMax = 0;
    let chronoUnitConversorMin = 0;
    const resultAll = reservations.get(propertyTicker).map(reservation => {
      const { inventories, reservationARI } = getEntitiesFromReservationARIMap({
        reservation,
        reservationARIMap,
      });
      const inventory = inventories
        .get(reservation.get("inventory"))
        .merge(
          reservationARI.get(propertyTicker).get("arivalues").get(reservation.get("inventory"))
        );
      const reservationPaymentTypes = reservationARI
        .get(propertyTicker)
        .get("conditions")
        .get(inventory.get("condition"))
        .get("paymentTypes");
      const checkinDate = Date.parse(reservation.get("startDate"));
      return reservationPaymentTypes.filter(element => {
        if (!element.get("maxRelease") || !element.get("minRelease")) {
          return true;
        }
        /** Here the Conversion from the cronoUnit selected to miliseconds is done*/
        switch (element.get("maxRelease") && element.get("maxRelease").get("chronoUnit")) {
          case "DAYS":
            chronoUnitConversorMax = daystoMilisecondsConvertor;
            break;
          case "HOURS":
            chronoUnitConversorMax = hourstoMilisecondsConvertor;
            break;
          default:
            chronoUnitConversorMax = daystoMilisecondsConvertor;
        }
        switch (element.get("minRelease") && element.get("minRelease").get("chronoUnit")) {
          case "DAYS":
            chronoUnitConversorMin = daystoMilisecondsConvertor;
            break;
          case "HOURS":
            chronoUnitConversorMin = hourstoMilisecondsConvertor;
            break;
          default:
            chronoUnitConversorMin = daystoMilisecondsConvertor;
        }
        // We allow the user to check in for today until 23:59:59
        const maximumCheckInDate = new Date(checkinDate);
        maximumCheckInDate.setDate(maximumCheckInDate.getDate() + 1);
        maximumCheckInDate.setHours(0, 0, 0, 0);
        maximumCheckInDate.setSeconds(maximumCheckInDate.getSeconds() - 1);

        // We get the amount of time remaining to final possible checkin
        const numberOfMillisecondsBeforeStay = maximumCheckInDate - Date.now();

        // We obtain the configured max release in milliseconds or a dummy if it's not configured
        const maxReleaseMilliseconds =
          element.get("maxRelease") && element.get("maxRelease").get("duration") > 0
            ? element.get("maxRelease").get("duration") * chronoUnitConversorMax
            : Number.MAX_VALUE;

        // We obtain the configured min release in milliseconds or a dummy if it's not configured
        const minReleaseMilliseconds =
          element.get("minRelease") && element.get("minRelease").get("duration") > 0
            ? element.get("minRelease").get("duration") * chronoUnitConversorMin
            : -Number.MAX_VALUE;

        /** Here we check if the min and max release are true and is a valid Payment type*/
        // MinRelease is valid if the amount of time before checking is larger than the configured amount
        const isMinReleaseValid = numberOfMillisecondsBeforeStay >= minReleaseMilliseconds;

        // MinRelease is valid if the amount of time before checking is less than the configured amount
        const isMaxReleaseValid = numberOfMillisecondsBeforeStay <= maxReleaseMilliseconds;

        return (
          isMinReleaseValid &&
          isMaxReleaseValid &&
          constants.PAYMENT_TYPES[element.get("type")] !== undefined
        );
      });
    });
    const result =
      resultAll?.get(0) &&
      resultAll?.get(0).filter(paymentMethod =>
        resultAll.every(it => {
          return it.some(payMethod => {
            return paymentMethod.get("ticker") === payMethod.get("ticker");
          });
        })
      );

    const paymentsOrder = [
      constants.PAYMENT_TYPES.WITHOUT_CARD,
      constants.PAYMENT_TYPES.RESERVATION_REQUEST,
      constants.PAYMENT_TYPES.DEFERRED,
      constants.PAYMENT_TYPES.TRANSFER,
      constants.PAYMENT_TYPES.PAY_NO_PAIN_PROXY,
      constants.PAYMENT_TYPES.CREDIT_CARD_VAULT,
      constants.PAYMENT_TYPES.PAY_NO_PAIN_V2,
      constants.PAYMENT_TYPES.POS,
      constants.PAYMENT_TYPES.CRIPTAN,
      constants.PAYMENT_TYPES.PAYPAL_EXPRESS_CHECKOUT,
    ];
    const orderedPayments =
      result &&
      new List(
        paymentsOrder.reduce((orderedPayments, paymentName) => {
          const payment = result.find(p => p.get("type") === paymentName);
          if (payment) {
            orderedPayments = [...orderedPayments, payment];
          }
          return orderedPayments;
        }, [])
      );
    if (orderedPayments && orderedPayments.size > 1) {
      const firstPayment = orderedPayments.get(0);
      return new List([...orderedPayments.shift(), firstPayment]);
    }
    return orderedPayments;
  }
);

export const paymentTypesSelector = createSelector(availablePaymentsSelector, availablePayments => {
  const availables =
    availablePayments &&
    availablePayments.map(element => {
      return element.get("ticker");
    });

  return availablePayments && new Map().set("available", availables);
});

export const selectedPaymentSelector = createSelector(
  availablePaymentsSelector,
  selectedPaymentTickerSelector,
  propertyTickerSelector,
  (availablePayments, selectedPaymentTicker, propertyTicker) => {
    let selected =
      availablePayments &&
      availablePayments
        .filter(
          element =>
            selectedPaymentTicker &&
            element.get("ticker") === selectedPaymentTicker.get(propertyTicker)
        )
        .first();
    if (!selectedPaymentTicker && availablePayments) {
      selected = availablePayments.last();
    }
    return availablePayments && new Map().set(propertyTicker, selected);
  }
);
function calculateServiceQuantity(service, guestsList) {
  if (!service.get("showedQuantity")) {
    if (service.get("type") === constants.SERVICE_TYPE.ROOM) {
      return service.get("dateSelectionType") === constants.DATE_SELECTION_TYPE.ALL
        ? service.get("totalRatePerDay").size
        : 1;
    } else if (service.get("type") === constants.SERVICE_TYPE.PERSON) {
      const totalGuests = service
        .get("appliesToGuests")
        .reduce((accummulator, guest) => accummulator + (guestsList[guest] || 0), 0);
      return service.get("dateSelectionType") === constants.DATE_SELECTION_TYPE.ALL
        ? totalGuests * service.get("totalRatePerDay").size
        : totalGuests;
    }
  }

  return service.get("showedQuantity");
}

export const guestsListSelector = createSelector(
  configurationSelector,
  reservationARIMapSelector,
  propertyTickerSelector,
  (configuration, reservationARIMap, propertyTicker) => {
    const result = {};
    if (reservationARIMap) {
      reservationARIMap.map((ari, reservationId) => {
        result[reservationId] = {};
        if (ari && ari.get(propertyTicker) && ari.get(propertyTicker).get("occupants")) {
          for (const occupant in constants.CONFIGURATION_OCCUPANTS) {
            if (constants.CONFIGURATION_OCCUPANTS.hasOwnProperty(occupant)) {
              result[reservationId][occupant] = parseInt(
                ari.get(propertyTicker).get("occupants").get(occupant),
                10
              );
            }
          }
        }
      });
    }
    return result;
  }
);

export const channelSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("channel")
);

export const affiliateSelector = createSelector(
  configurationSelector,
  configuration => configuration && configuration.get("affiliate")
);

export const cityTaxConfigurationSelector = createSelector(
  channelSelector,
  affiliateSelector,
  getCityTaxConfiguration
);

function calculateStayDays({ currentStartDate, currentEndDate }) {
  const ariStart = moment(currentStartDate).startOf("day").toDate();
  const ariEnd = moment(currentEndDate).startOf("day").toDate();
  return dateDiffInDays(ariStart, ariEnd);
}

export const cartSelector = createSelector(
  reservationARIMapSelector,
  reservationsSelector,
  propertyTickerSelector,
  paymentTypesSelector,
  selectedPaymentSelector,
  defaultCurrencySelector,
  currencySelector,
  currencyExchangeSelector,
  addObligatoryExtrasRateToRoomSelectionSelector,
  guestsListSelector,
  restoredSessionSelector,
  cityTaxConfigurationSelector,
  (
    reservationARIMap,
    reservations,
    propertyTicker,
    paymentTypes,
    selectedPayment,
    defaultCurrency,
    currency,
    currencyExchange,
    addObligatoryExtrasRateToRoomSelection,
    guestsList,
    restoredSession,
    cityTaxConfiguration
  ) => {
    if (!reservations || !reservations.get(propertyTicker) || !currency || !currencyExchange) {
      return undefined;
    }
    const hidePrice = (restoredSession && restoredSession.hidePrice) || false;
    // TODO: USE CURRENCY MULTIPLIER HERE!!
    // TODO: THERE'S A BIG PROBLEM HERE, RESERVATION SERVICES AND INVENTORIES MAY NOT BE PRESENT ON MULTIPLE RESERVATIONS / (DATES CHANGE => BOOKABLES ARE UNAVAILABLE)
    // WHAT COULD BE DONE INSTEAD IS TO CREATE A SECOND ARIVALUE: RESERVATION DATA , AN OBJECT WHICH HAS ALL RESERVATION INVENTORIES AND SERVICES
    let bookingTotalRate = 0;
    let depositTotalRate = 0;
    let bookingOriginalRate = 0;
    let depositOriginalRate = 0;
    let bookingTotalRateWithCityTax = 0;
    let bookingOriginalTotalRateWithCityTax = 0;
    let originalTotalTax = 0;
    let originalTotalCityTax = 0;

    // List of all distinct conditions that has reservation
    const filteredConditions = {};
    const startDateList = {};
    let paymentType = null;
    let selectedPackage = {};
    const denormalizedReservations = reservations
      .get(propertyTicker)
      .map(reservation => {
        const {
          inventories,
          services,
          reservationARI,
          packages,
        } = getEntitiesFromReservationARIMap({
          reservation,
          reservationARIMap,
        });
        const bookableCurrency = getCurrencyFromReservationARIMap({
          reservation,
          reservationARIMap,
        });
        const exchangeMultiplier = getExchangeMultiplier(
          bookableCurrency,
          currency,
          currencyExchange
        );
        const numberOfUnits = reservation.get("numberOfUnits");
        const days = calculateStayDays({
          currentStartDate: reservation.get("startDate"),
          currentEndDate: reservation.get("endDate"),
        });
        let inventory = inventories
          .get(reservation.get("inventory"))
          .merge(
            reservationARI.get(propertyTicker).get("arivalues").get(reservation.get("inventory"))
          );
        if (reservation.get("selectedPackage")) {
          selectedPackage = packages.get(reservation.get("selectedPackage")).merge(
            reservationARI
              .get(propertyTicker)
              .get("arivalues")
              .get(`package_${reservation.get("selectedPackage")}`)
          );
        } else {
          selectedPackage = undefined;
        }
        /* TODO: sort services by order */
        // propertyData.get(ARI.get('ticker')).merge(ARI)
        inventory = inventory.set(
          "services",
          (inventory.get("services") &&
            inventory
              .get("services")
              .map(
                ticker =>
                  services.get(ticker) &&
                  transformBookableWithCurrencyConversion(
                    services
                      .get(ticker)
                      .merge(reservationARI.get(propertyTicker).get("arivalues").get(ticker)),
                    currency,
                    currencyExchange
                  )
              )
              .filter(service => !!service)) ||
            []
        );
        inventory = inventory.set(
          "accommodation",
          reservationARI
            .get(propertyTicker)
            .get("accommodations")
            .get(inventory.get("accommodation"))
        );
        inventory = inventory.set(
          "mealplan",
          reservationARI.get(propertyTicker).get("mealplans").get(inventory.get("mealplan"))
        );
        inventory = inventory.set(
          "configuration",
          reservationARI
            .get(propertyTicker)
            .get("configurations")
            .get(inventory.get("configuration"))
        );
        inventory = inventory.set(
          "condition",
          reservationARI.get(propertyTicker).get("conditions").get(inventory.get("condition"))
        );
        inventory = transformBookableWithCurrencyConversion(inventory, currency, currencyExchange);

        filteredConditions[inventory.get("condition").get("ticker")] = inventory.get("condition");
        startDateList[reservation.get("startDate")] = true;

        const totalOccupancy = inventory
          .get("configuration")
          .get("guests")
          .reduce((accumulator, guest) => {
            return accumulator + guest.get("quantity");
          }, 0);
        // TODO: THIS IS WRONG! THE RESERVATION SHOULD STORE THE RATE FOR EACH SERVICE! BECAUSE THEY COULD VARY FOR EACH DAY.
        const reservationTotalOccupancy = totalOccupancy * reservation.get("numberOfUnits");
        const selectedServices = reservation
          .get("selectedServices")
          .map((service, ticker) => {
            let mergedService =
              services.get(ticker) &&
              services
                .get(ticker)
                .merge(reservationARI.get(propertyTicker).get("arivalues").get(ticker))
                .merge(service);
            const inventoryConfigurationTicker =
              inventory &&
              reservationARI.get(propertyTicker) &&
              reservationARI.get(propertyTicker).get("inventories") &&
              reservationARI
                .get(propertyTicker)
                .get("inventories")
                .get(inventory.get("ticker"))
                .get("configuration");
            let guests =
              reservationARI.get(propertyTicker) &&
              reservationARI.get(propertyTicker).get("configurations") &&
              reservationARI
                .get(propertyTicker)
                .get("configurations")
                .get(inventoryConfigurationTicker) &&
              reservationARI
                .get(propertyTicker)
                .get("configurations")
                .get(inventoryConfigurationTicker)
                .get("guests");

            guests = Object.entries(guests.toJS()).reduce(
              (acc, curr) => (acc = { ...acc, [curr[0]]: curr[1].quantity }),
              {}
            );
            mergedService =
              mergedService &&
              mergedService.set("showedQuantity", calculateServiceQuantity(mergedService, guests));

            return (
              mergedService &&
              transformBookableWithCurrencyConversion(mergedService, currency, currencyExchange)
            );
          })
          .filter(service => !!service);
        const serviceRate = calculateServicesRate({
          services: selectedServices,
          rooms: reservation.get("numberOfUnits"),
          stayRate: inventory.get("originalRates").get("originalRate"),
          defaultCurrency,
          currencyExchange,
          inventoryTicker: inventory.get("ticker"),
          propertyARI: reservationARI.get(propertyTicker),
        });
        let obligatoryServiceRate = 0;
        let negativeServicesTotalAmount = 0;
        let obligatoryNegativeServicesTotalAmount = 0;
        const serviceRatesInfo =
          fromJS(
            selectedServices.reduce((accumulator, service) => {
              const calculatedServiceRate = calculateServiceRate({
                service,
                rooms: reservation.get("numberOfUnits"),
                transformCurrency: false,
                stayRate: inventory.get("originalRates").get("originalRate"),
                defaultCurrency,
                currencyExchange,
                inventoryTicker: inventory.get("ticker"),
                propertyARI: reservationARI.get(propertyTicker),
              });
              accumulator[service.get("ticker")] = calculatedServiceRate;
              if (calculatedServiceRate < 0) {
                negativeServicesTotalAmount += calculatedServiceRate;
              }
              if (service.get("obligatory") && addObligatoryExtrasRateToRoomSelection) {
                if (calculatedServiceRate < 0) {
                  obligatoryNegativeServicesTotalAmount += calculatedServiceRate;
                }
                obligatoryServiceRate += calculatedServiceRate;
              }
              return accumulator;
            }, {})
          ) || new Map();

        const stayRate = hidePrice ? 0 : inventory.get("originalRates").get("totalRate");
        const baseRate = hidePrice ? 0 : inventory.get("originalRates").get("originalRate");
        const discountAmount = !hidePrice && inventory.get("originalRates").get("discountAmount");
        // TODO: it would be cleaner if we recalculated this from the reservation ARI
        const totalRate = hidePrice ? serviceRate : stayRate * numberOfUnits + serviceRate;
        const condition = inventory.get("condition");
        paymentType = condition
          .get("paymentTypes")
          .filter(element => constants.PAYMENT_TYPES[element.get("type")] !== undefined)
          .find(
            conditionPaymentType =>
              constants.PAYMENT_TYPES[conditionPaymentType && conditionPaymentType.get("type")] ===
              (selectedPayment &&
                selectedPayment.get(propertyTicker) &&
                selectedPayment.get(propertyTicker).get("type"))
          );
        if (paymentType === null) {
          return null;
        }
        // We need to consider the negativeServicesTotalAmount
        // to calculate the first night cost deposit since it acts as a discount
        const depositRate = hidePrice
          ? 0
          : calculateDeposit(
              totalRate,
              paymentType && paymentType.get("paymentPercentage"),
              condition.get("payFirstNight"),
              condition.get("earlyCharge"),
              condition.get("minimumCharge"),
              inventory.get("firstNightCost"),
              reservation.get("numberOfUnits"),
              negativeServicesTotalAmount
            );
        bookingTotalRate += totalRate * exchangeMultiplier;
        bookingOriginalRate += totalRate;
        depositTotalRate += depositRate * exchangeMultiplier;
        depositOriginalRate += depositRate;
        const originalTax = Math.round(totalRate * TAX_MULTIPLIER * 100) / 100;
        const inventoryCityTax =
          cityTaxConfiguration !== constants.CITY_TAX_CONFIGURATION.EXCLUDE_FROM_ALL_STEPS
            ? Math.round(inventory.get("cityTax") * 100) / 100
            : 0;
        originalTotalTax += originalTax;
        originalTotalCityTax += inventoryCityTax;
        bookingTotalRateWithCityTax = bookingTotalRate + inventoryCityTax * exchangeMultiplier;
        bookingOriginalTotalRateWithCityTax = bookingOriginalRate + inventoryCityTax;
        // We need to add only the obligatory negative extras to get the base amount
        // not all negative extras since they are ADDED to the base rate
        const reservationBaseRate =
          baseRate * numberOfUnits +
          obligatoryServiceRate +
          Math.abs(obligatoryNegativeServicesTotalAmount);
        const firstNightCost =
          inventory.getIn(["firstNightCost"]) !== null && inventory.getIn(["firstNightCost"]) > 0
            ? inventory.getIn(["firstNightCost"]) + obligatoryServiceRate / days
            : 0;
        return reservation
          .set("inventory", inventory)
          .set("totalOccupancy", reservationTotalOccupancy)
          .set("selectedServices", selectedServices)
          .set("serviceRate", serviceRate * exchangeMultiplier)
          .set("deposit", depositRate * exchangeMultiplier)
          .set("pending", (totalRate - depositRate) * exchangeMultiplier)
          .set("serviceRatesInfo", serviceRatesInfo)
          .set("baseRate", reservationBaseRate * exchangeMultiplier)
          .set(
            "discountAmount",
            discountAmount !== false &&
              discountAmount !== 0 &&
              discountAmount !== null &&
              discountAmount !== undefined
              ? discountAmount * numberOfUnits * exchangeMultiplier
              : undefined
          )
          .set("totalRate", totalRate * exchangeMultiplier)
          .set("originalTotalRate", totalRate)
          .set("cityTax", inventory.get("cityTax") * exchangeMultiplier)
          .set("originalCityTax", inventory.get("cityTax"))
          .set("originalTax", originalTax)
          .set("selectedPackage", selectedPackage)
          .set("firstNightCost", firstNightCost);
      })
      .filter(it => it !== null);
    depositTotalRate = hidePrice ? 0 : depositTotalRate;
    depositOriginalRate = hidePrice ? 0 : depositOriginalRate;
    const pendingTotal = bookingTotalRateWithCityTax - depositTotalRate;
    const pendingOriginal = bookingOriginalTotalRateWithCityTax - depositOriginalRate;

    const expiration = denormalizedReservations.reduce((accumulator, reservation) => {
      const expirationDate =
        reservation.get("inventory") &&
        reservation.get("inventory").get("condition") &&
        reservation.get("inventory").get("condition").get("deferredInfo") &&
        reservation.get("inventory").get("condition").get("deferredInfo").get("expiration");
      if (!accumulator) return expirationDate;
      if (!expirationDate) return accumulator;
      return moment(expirationDate).isBefore(accumulator) ? expirationDate : accumulator;
    }, undefined);
    return {
      reservations: denormalizedReservations,
      pendingTotal,
      bookingTotalRate,
      depositTotal: depositTotalRate,
      bookingOriginalRate,
      depositOriginalRate,
      pendingOriginal,
      filteredConditions,
      startDateList,
      hidePrice: hidePrice,
      expiration,
      originalTotalTax,
      originalTotalCityTax,
      bookingOriginalTotalRateWithCityTax,
    };
  }
);

export const reservationTotalAmountSelector = createSelector(cartSelector, cart => {
  if (!cart || !cart.reservations) {
    return null;
  }
  return cart.reservations.reduce(
    (accumulator, reservation) =>
      accumulator + reservation.get("totalRate") * reservation.get("numberOfUnits"),
    0
  );
});

export const extrasQuestionnaireSelector = createSelector(
  propertyDataSelector,
  propertyTickerSelector,
  (propertyData, propertyTicker) =>
    getSpecificQuestionnaireType(
      propertyData,
      propertyTicker,
      constants.QUESTIONNAIRE_DEPENDER_TYPE.EXTRA,
      depender => depender && !!depender.get("tickers")
    )
);

export const guestFormQuestionnaireSelector = createSelector(
  propertyDataSelector,
  propertyTickerSelector,
  (propertyData, propertyTicker) =>
    getSpecificQuestionnaireType(
      propertyData,
      propertyTicker,
      constants.QUESTIONNAIRE_DEPENDER_TYPE.GUEST_FORM
    )
);
